/**
 * Memory, het tweede spelletje in het startmenu.
 *
 * De spelregels staan in lib/memory.js; hier staat alles wat met het scherm te
 * maken heeft: welke krabbels meedoen, de klok die een misser weer dichtdoet, de
 * geluidjes en de stijlen.
 *
 * Het speelveld staat wel in this.state, anders dan bij Krabbelworm. Daar tikt
 * een klok veertien keer per seconde en dan is een setState per stap te duur;
 * hier draai je een kaartje om als je er zelf op tikt, en dat is een paar keer
 * per minuut.
 */

import { api, asJson } from '../lib/api.js';
import { PB_URL } from '../lib/config.js';
import { playMemDraai, playMemKlaar, playMemPaar } from '../lib/sound.js';
import * as mem from '../lib/memory.js';
import * as store from '../lib/store.js';

/** Hoe lang een misser open blijft liggen voordat hij weer omdraait. */
const MISSER_MS = 900;

/** De collectie die er op de server bij moet; zie CLAUDE.md, sectie 15. */
const COL = 'paal_memory';

/**
 * Hoe je twee beurten met elkaar vergelijkt.
 *
 * Minder zetten is beter, en bij gelijk aantal zetten de snelste. Dat tweede is
 * geen sier: foutloos is tien zetten en dat haalt bijna iedereen weleens, dus
 * zonder de tijd staat de halve paal op een gedeelde eerste plek.
 *
 * Levert negatief als a beter is dan b, zoals een sorteerfunctie hoort te doen.
 */
const beter = (a, b) => (a.zetten !== b.zetten ? a.zetten - b.zetten : (a.ms || 0) - (b.ms || 0));

/** Een tijd om te lezen: onder de minuut met een decimaal, daarboven m:ss. */
function tijdTekst(ms) {
  if (!ms) return '';
  const sec = ms / 1000;
  if (sec < 60) return sec.toFixed(1).replace('.', ',') + ' s';
  const m = Math.floor(sec / 60);
  return m + ':' + String(Math.round(sec - m * 60)).padStart(2, '0');
}

export default {
  /**
   * De krabbels die mee mogen doen: van de paal, stilstaand en vierkant.
   *
   * Een bewegende krabbel verraadt zichzelf doordat hij beweegt, en dan is het
   * geen memory meer. Breed mag ook niet: die zijn 64 bij 32 en zouden als enige
   * kaartje niet vierkant zijn. Breed is een krabbel die je samen maakte
   * (coAuthorId) of waarvan de afmeting gemeten is; zie isWide in render-vals.
   */
  memKrabbels() {
      const breed = this.state.wideIds || {};
      return (this.state.galleryItems || [])
        .filter((g) => g && g.src && (Number(g.frames) || 1) <= 1 && !g.coAuthorId && !breed[g.id])
        .map((g) => g.id);
    },

  /** Van id naar de krabbel zelf, voor het plaatje en de titel op een kaartje. */
  memKrabbel(id) {
      return (this.state.galleryItems || []).find((g) => g.id === id) || null;
    },

  openMemory() {
      this.memBest();
      this.fetchMemScores();
      this.setState({ dialog: 'memory', notifOpen: false, startOpen: false });
      this.memStart();
      // Kom je hier via het adres binnen, dan is de paal misschien nog niet
      // geladen en zijn er dus nog geen krabbels om op de kaartjes te zetten. Eén
      // keer opnieuw proberen zodra die er wel zijn; daarna is het aan de knop.
      clearTimeout(this.memWacht);
      if (!this.state.memSpel) {
        this.memWacht = setTimeout(() => {
          if (this.state.dialog === 'memory' && !this.state.memSpel) this.memStart();
        }, 1500);
      }
    },

  closeMemory() {
      clearTimeout(this.memTimer);
      clearTimeout(this.memWacht);
      this.memTimer = null;
      this.setState({ dialog: null, memSpel: null });
    },

  memStart() {
      clearTimeout(this.memTimer);
      // De klok begint pas bij je eerste tik, niet bij het neerleggen: anders
      // kost het openen van het venster je al tijd.
      this.memBegin = 0;
      const krabbels = this.memKrabbels();
      if (krabbels.length < mem.PAREN_MIN) { this.setState({ memSpel: null }); return; }
      this.setState({ memSpel: mem.nieuwSpel(krabbels, mem.PAREN), memMs: 0 });
    },

  /**
   * Op een kaartje tikken.
   *
   * Draai je er twee die niet bij elkaar horen, dan blijven ze even open liggen:
   * zonder die pauze zie je het tweede kaartje niet en is er niets te onthouden.
   * De klok die ze dichtdoet loopt hier, want de regels in lib/memory.js weten
   * niets van tijd.
   */
  memDraai(i) {
      const spel = this.state.memSpel;
      if (!spel || this.memTimer) return;
      const na = mem.draai(spel, i);
      if (na === spel) return;
      if (!this.memBegin) this.memBegin = Date.now();
      this.setState({ memSpel: na });

      if (na.klaar) {
        const ms = Date.now() - this.memBegin;
        this.spelGeluid(playMemKlaar);
        this.setState({ memMs: ms });
        this.memBest(na.zetten, ms);
        this.memStuurScore(na.zetten, ms);
        return;
      }
      if (mem.misser(na)) {
        this.spelGeluid(playMemDraai);
        this.memTimer = setTimeout(() => {
          this.memTimer = null;
          this.setState({ memSpel: mem.sluit(this.state.memSpel) });
        }, MISSER_MS);
        return;
      }
      // Twee weg van het bord is een paar; één open is gewoon omdraaien.
      this.spelGeluid(na.open.length ? playMemDraai : playMemPaar);
    },

  /**
   * Je record in deze browser: de beste beurt die je ooit had.
   *
   * Alleen een volle beurt telt. Een spel met minder paren, omdat er nog te
   * weinig stilstaande krabbels aan de paal hangen, is niet met een volle beurt
   * te vergelijken.
   */
  memBest(zetten, ms) {
      let best = 0, bestMs = 0;
      try {
        best = Number(store.get('gp_mem_best') || 0) || 0;
        bestMs = Number(store.get('gp_mem_best_ms') || 0) || 0;
      } catch (e) {}
      const vol = (this.state.memSpel || {}).kaarten;
      if (zetten && (!vol || vol.length === mem.PAREN * 2)) {
        if (!best || beter({ zetten, ms }, { zetten: best, ms: bestMs }) < 0) {
          best = zetten; bestMs = ms || 0;
          try {
            store.set('gp_mem_best', String(best));
            store.set('gp_mem_best_ms', String(bestMs));
          } catch (e) {}
        }
      }
      this.setState({ memBest: best, memBestMs: bestMs });
      return best;
    },

  // ---- de ranglijst -------------------------------------------------------

  async fetchMemScores() {
      if (!PB_URL) return;
      try {
        // Oplopend sorteren, en de tijd als tweede sleutel: hier is minder beter.
        const res = await api(`${PB_URL}/api/collections/${COL}/records?sort=zetten,ms&perPage=10`);
        // 404 = de collectie bestaat nog niet, 403 = hij bestaat maar de regels
        // staan dicht. Dat is niet hetzelfde, dus we houden de code apart.
        if (!res.ok) { this.setState({ memMissing: true, memStatus: res.status }); return; }
        const data = await res.json();
        // Regels zonder zetten doen niet mee. Die staan er: de collectie is als
        // kopie van paal_snake gemaakt, dus hij had een veld `score` en geen
        // `zetten`, en vijf mensen hebben in die tijd gespeeld. Hun regel werd wel
        // aangemaakt maar hun zetten en tijd gooide de server weg, want die velden
        // bestonden niet. Zonder deze zeef komen die vijf bovenaan te staan met een
        // nul: een perfecte beurt die niemand gespeeld heeft.
        const top = (data.items || [])
          .map((r) => ({
            id: r.authorId || r.id, name: r.name || '', zetten: Number(r.zetten) || 0, ms: Number(r.ms) || 0
          }))
          .filter((r) => r.zetten > 0);
        this.setState({ memTop: top, memMissing: false, memStatus: 0 });
      } catch (e) {
        this.setState({ memMissing: true, memStatus: 0 });
      }
    },

  /**
   * Alleen je beste beurt gaat naar de server, en per persoon staat er één regel.
   * Een beurt met minder paren doet niet mee: die is niet te vergelijken.
   */
  async memStuurScore(zetten, ms) {
      const id = this.state.accountId;
      const vol = (this.state.memSpel || {}).kaarten;
      if (!PB_URL || !id || !zetten) return;
      if (vol && vol.length !== mem.PAREN * 2) return;
      const naam = this.state.author || '';
      try {
        const zoek = await api(`${PB_URL}/api/collections/${COL}/records?filter=(authorId='${id}')&perPage=1`);
        if (!zoek.ok) { this.setState({ memMissing: true, memStatus: zoek.status }); return; }
        const mijn = ((await zoek.json()).items || [])[0];
        const oudZetten = Number(mijn && mijn.zetten) || 0;
        // Een regel zonder zetten is geen score maar een lege regel, en die mag je
        // overschrijven. Zonder die uitzondering leest nul zetten als de perfecte
        // beurt, want minder is hier beter, en dan komt er bij die persoon nooit
        // meer iets in: elke echte beurt verliest van die nul. Precies dat is de
        // vijf mensen overkomen die speelden toen de collectie nog `score` had in
        // plaats van `zetten` en `ms`.
        if (mijn && oudZetten > 0 && beter({ zetten, ms }, { zetten: oudZetten, ms: Number(mijn.ms) || 0 }) >= 0) return;
        const res = mijn
          ? await api(`${PB_URL}/api/collections/${COL}/records/${mijn.id}`, asJson('PATCH', { zetten, ms, name: naam }))
          : await api(`${PB_URL}/api/collections/${COL}/records`, asJson('POST', { authorId: id, name: naam, zetten, ms }));
        if (!res.ok) { this.setState({ memMissing: true, memStatus: res.status }); return; }
        this.fetchMemScores();
      } catch (e) {
        this.setState({ memMissing: true, memStatus: 0 });
      }
    },

  memVals() {
      const mob = this.state.isMobile;
      const ink = 'var(--ink)';
      const spel = this.state.memSpel;
      const genoeg = this.memKrabbels().length >= mem.PAREN_MIN;
      // Vijf kolommen van vier. Het kaartje is een vijfde van de breedte min de
      // kieren ertussen; op een telefoon krimpt het mee met de behuizing, en met
      // een verhouding van 1 blijft het vierkant, net als de krabbel erop.
      const kier = 8;
      return {
        showMemory: this.state.dialog === 'memory',
        openMemory: this.openMemory,
        closeMemory: this.closeMemory,
        memTitel: 'Minigame: Krabbelry!',
        memTitelStyle: {
          fontSize: mob ? '17px' : '19px', fontWeight: 700, letterSpacing: '-.01em', flex: '1 1 auto'
        },
        /**
         * Het venster is de behuizing, net als bij Krabbelworm. Er lagen twee
         * kaders over elkaar met elk een eigen rand en schaduw; nu is de lila plaat
         * het venster zelf. Zie snakePanelStyle in logic/snake.js.
         */
        memPanelStyle: {
          width: mob ? '100%' : 'min(440px,100%)', maxHeight: '90dvh',
          overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain',
          background: 'var(--lila-diep)',
          padding: mob ? '12px' : '14px', display: 'flex', flexDirection: 'column', gap: '12px', boxSizing: 'border-box'
        },
        memConsoleStyle: {
          flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '12px',
          minWidth: '0', boxSizing: 'border-box'
        },
        memRasterStyle: {
          display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: kier + 'px',
          minWidth: '0'
        },
        memKaarten: (spel ? spel.kaarten : []).map((k, i) => {
          const zichtbaar = mem.zichtbaar(spel, i);
          const gevonden = spel.gevonden.indexOf(i) >= 0;
          const krabbel = this.memKrabbel(k.krabbel);
          return {
            zichtbaar,
            src: krabbel ? krabbel.src : '',
            titel: zichtbaar && krabbel ? krabbel.title : 'Draai dit kaartje om',
            onClick: () => this.memDraai(i),
            style: {
              position: 'relative', width: '100%', aspectRatio: '1', height: 'auto', padding: '0',
              // De ronding mee laten krimpen, anders is een kaartje op een smal
              // scherm een cirkel; zie ook de koppen bij "wie krabbelde vandaag".
              borderRadius: 'min(12px, 22%)', overflow: 'hidden', justifyContent: 'center',
              background: zichtbaar ? '#fff' : 'var(--lila)',
              // Een gevonden paar blijft liggen maar hoeft niet meer te schreeuwen.
              opacity: gevonden ? '.72' : '1',
              cursor: zichtbaar ? 'default' : 'pointer'
            },
            imgStyle: {
              width: '100%', aspectRatio: '1', height: 'auto', objectFit: 'cover',
              imageRendering: 'pixelated', display: 'block'
            },
            achterStyle: {
              fontSize: '18px', fontWeight: 700, color: 'var(--paars)', lineHeight: 1
            }
          };
        }),
        memKlaar: !!(spel && spel.klaar),
        showMemOver: !!(spel && spel.klaar),
        memOverStyle: {
          position: 'absolute', inset: '0', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: '9px',
          padding: '10px', boxSizing: 'border-box',
          background: 'rgba(255,255,255,.72)'
        },
        memPlakStyle: {
          flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '6px',
          width: 'max(67%, 168px)', maxWidth: '100%', padding: '10px 12px', boxSizing: 'border-box',
          background: '#fff', border: '3px solid ' + ink, borderRadius: '12px',
          boxShadow: '4px 4px 0 ' + ink
        },
        memOverKopStyle: { fontSize: '26px', fontWeight: 700, lineHeight: 1.15, textAlign: 'center' },
        memOverRegelStyle: {
          display: 'flex', alignItems: 'baseline', justifyContent: 'flex-start', gap: '6px', flexWrap: 'wrap'
        },
        memOverLabelStyle: { fontSize: '14px', fontWeight: 600, color: 'var(--tekst-3)' },
        memOverGetalStyle: { fontSize: '14px', fontWeight: 700 },
        memZettenLabel: 'In ' + (spel ? spel.zetten : 0) + (spel && spel.zetten === 1 ? ' zet' : ' zetten')
          + (this.state.memMs ? ', ' + tijdTekst(this.state.memMs) : ''),
        memBestLabel: this.state.memBest
          ? 'Jouw record: ' + this.state.memBest + ' zetten'
            + (this.state.memBestMs ? ' in ' + tijdTekst(this.state.memBestMs) : '')
          : '',
        showMemBest: !!this.state.memBest,
        /**
         * De ranglijst, in dezelfde roze pilletjes als bij Krabbelworm. Lager is
         * hier beter, dus wat je ziet staan is het laagste aantal zetten en bij
         * gelijke stand de snelste tijd.
         */
        memTop: (this.state.memTop || []).slice(0, 5).map((r, n) => {
          const ikzelf = r.id === (this.state.accountId || '');
          const medaille = ['\uD83E\uDD47', '\uD83E\uDD48', '\uD83E\uDD49'][n] || '';
          return {
            plek: medaille || String(n + 1),
            name: r.name || 'Iemand',
            score: r.zetten + (r.ms ? ' \u00b7 ' + tijdTekst(r.ms) : ''),
            ikzelf,
            rijStyle: {
              display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 9px', boxSizing: 'border-box',
              border: '2px solid ' + ink, borderRadius: '999px',
              background: ikzelf ? 'var(--roze)' : 'var(--roze-balk)',
              color: ikzelf ? '#fff' : ink
            },
            plekStyle: medaille
              ? { fontSize: '13px', flex: '0 0 auto', lineHeight: 1 }
              : { fontSize: '11px', fontWeight: 700, flex: '0 0 auto', width: '13px', textAlign: 'center', opacity: '.65' },
            naamStyle: { fontSize: '13px', fontWeight: 700, flex: '1 1 auto', minWidth: '0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
            scoreStyle: { fontSize: '12px', fontWeight: 700, flex: '0 0 auto' }
          };
        }),
        memLijstStyle: { display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '0' },
        /**
         * Waarom is de lijst leeg? Drie antwoorden, en ze betekenen alle drie iets
         * anders.
         *
         * 403: de collectie bestaat, maar de regels staan dicht.
         * 400: er wordt gesorteerd op zetten en ms, en die velden kent de server
         *      niet. Dat komt doordat de collectie er niet is of doordat de velden
         *      anders heten. PocketBase geeft hier geen 404 voor, want de vraag
         *      zelf is fout zodra je op een onbekend veld sorteert.
         * 404: de collectie bestaat niet, bij een vraag zonder sortering.
         */
        memLijstLeeg: this.state.memStatus === 403
          ? 'De ranglijst mag nog niet gelezen worden; de regels van de collectie staan dicht.'
          : this.state.memStatus === 400
            ? 'De ranglijst staat nog niet klaar: de collectie paal_memory moet de velden zetten en ms hebben.'
            : this.state.memMissing
              ? 'De ranglijst staat nog niet op de server.'
              : 'Nog geen beurten.',
        showMemLijstLeeg: !(this.state.memTop || []).length,
        memLijstLeegStyle: { fontSize: '12px', fontWeight: 600, color: 'var(--tekst-3)', lineHeight: 1.4, textAlign: 'center' },
        // Onder het raster stond een balk met zetten, te gaan en je record. Die is
        // eruit: hoe je het deed hoort een verrassing te zijn tot je klaar bent, en
        // een teller die meeloopt maakt van onthouden een rekensom.
        memOpnieuw: this.memStart,
        memOpnieuwLabel: spel && spel.klaar ? 'Opnieuw' : 'Nieuwe ronde',
        memOpnieuwStyle: {
          flex: '0 0 auto', width: '100%', height: '48px', boxSizing: 'border-box',
          justifyContent: 'center', borderRadius: '12px', fontSize: '16px', fontWeight: 600
        },
        // Te weinig stilstaande krabbels aan de paal? Dan zeggen we waarom.
        showMemLeeg: !genoeg,
        memLeeg: 'Er hangen nog te weinig stilstaande krabbels aan de paal om te spelen. Er zijn er minstens twee nodig.',
        memLeegStyle: { fontSize: '13px', fontWeight: 600, color: 'var(--tekst-3)', lineHeight: 1.4, textAlign: 'center' }
      };
    }
};
